import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AttachableDocument } from "../components/templateForm/AttachmentsField";

/** PostgREST truncates any response to its max-rows cap (1000 by default),
 *  so "every document" must be paged for explicitly. Termination is on an
 *  empty page, not a short one, so a cap below PAGE_SIZE can't silently
 *  truncate the list (which seeds document numbering). */
const PAGE_SIZE = 1000;

/**
 * Every document the user can see in the project — the template form's
 * numbering seed and its attachment picker must see the whole project, not
 * whichever page of the documents list is on screen (that list is server-
 * filtered by the active search and folder, so seeding from it collides on
 * numbers and hides most of the project from "Choose file from project").
 */
export function useTemplateFormDocuments(projectId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["template-form-documents", projectId],
    enabled: enabled && projectId !== null,
    queryFn: async ({ signal }): Promise<AttachableDocument[]> => {
      const rows: AttachableDocument[] = [];
      for (let from = 0; ; ) {
        const { data, error } = await supabase
          .from("documents")
          .select("id, filename")
          .eq("project_id", Number(projectId))
          .eq("is_deleted", false)
          .eq("is_latest_version", true)
          // The visibility gates the canonical documents list applies —
          // chatbot/issue attachments (WHA-3330) and DWG-converted child PDFs
          // are hidden, and containers disappear once UNZIPPED (their extracted
          // children show instead, so container children stay in).
          .eq("hidden", false)
          .or("is_dwg_child.is.null,is_dwg_child.eq.false")
          .or("container_type.is.null,state.neq.UNZIPPED")
          // Paging is only stable over a deterministic order.
          .order("id")
          .range(from, from + PAGE_SIZE - 1)
          // Unmount (form closed, template switched) cancels the scan instead
          // of paging the whole project to nowhere.
          .abortSignal(signal);
        if (error) throw new Error(error.message);
        const page = data ?? [];
        rows.push(...page.map((row) => ({ id: String(row.id), filename: row.filename })));
        if (page.length === 0) return rows;
        from += page.length;
      }
    },
  });
}
